
  import { computed, defineComponent, ref } from 'vue';
  import { ResourceTable } from '@/ui/components/table/ResourceTable';
  import StaticPagination from '@/ui/components/StaticPagination.vue';
  import { ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import { useAttributes, useMetrics, useWidgetFormItem } from '@/ui/concerns/analytics/widget';
  import FormItemTitle from '@/ui/components/analytics/displays/FormItemTitle.vue';
  import { Loading } from '@/ui/components/Loading';
  import { du } from '@/ui/i18n/i18n';
  import { DetailedViewOptions, useDetailedViewStore } from '@/stores/analytics/detailedView';
  import { useDetailedView } from '@/ui/concerns/analytics/useDetailedView';
  import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';
  import FileItemsModal from '@/ui/components/analytics/modals/FileItemsModal.vue';

  interface TableProps {
    data: unknown;
    page: number;
    perPage: number;
    widget: ReportWidget;
  }

  type RowData = {
    id: string | number;
    label?: string;
    response?: string | number;
  };

  export default defineComponent<TableProps>({
    components: {
      FormItemTitle,
      ResourceTable,
      Pagination: StaticPagination,
      Loading,
      FileItemsModal,
    },
    props: ['data', 'page', 'perPage', 'widget'],
    emits: ['page-changed', 'detailed-view-open'],
    setup(props, ctx) {
      const { isGuest, reportId, report } = useReportInjection();
      const isFilesModalOpened = ref(false);
      const fileModalData = ref<string | null>(null);
      const { getMetricsWithLabels } = useMetrics();
      const { allAttributeLabels } = useAttributes();
      const {
        isResponseAttributeSelected,
        isDownloadable,
        canHaveMultipleAssets,
        isFileAsset,
        isDownloadingImage,
        downloadAsset,
        countAssets,
      } = useWidgetFormItem(props.widget);
      const store = useDetailedViewStore();
      const { getDetailedViewType } = useDetailedView();

      const attributeLabel = computed(() => props.widget.attribute ? allAttributeLabels[props.widget.attribute] : '');
      const metricLabel = computed(() => getMetricsWithLabels()
        .find(metric => metric.value === props.widget.metric)?.label ?? '');

      const onPageChanged = (value: number) => {
        ctx.emit('page-changed', value);
      };

      const downloadClick = (value: string) => {
        downloadAsset(value);
      };

      const showFiles = (value: string) => {
        fileModalData.value = value;
        isFilesModalOpened.value = true;
      };

      const closeFilesModal = () => {
        isFilesModalOpened.value = false;
        fileModalData.value = null;
      };

      const onRowClick = (row: RowData) => {
        if (isGuest) {
          return;
        }

        if (!(report.value && props.widget.metric)) {
          return;
        }

        let resourceId = row.id;
        const options: DetailedViewOptions = { head: row.label };

        if (isResponseAttributeSelected.value) {
          options.head = row.response;
          resourceId = props.widget.filters.formItem!;
        }

        store.setDetailedView({
          reportId: reportId.value ?? undefined,
          report: report.value,
          resourceId,
          widgetKey: props.widget.key,
          detailedViewType: getDetailedViewType(props.widget.metric),
          options,
        });
        ctx.emit('detailed-view-open');
      };

      return {
        attributeLabel,
        metricLabel,
        isFilesModalOpened,
        fileModalData,
        onPageChanged,
        isResponseAttributeSelected,
        isDownloadable,
        canHaveMultipleAssets,
        isFileAsset,
        isDownloadingImage,
        downloadClick,
        showFiles,
        closeFilesModal,
        countAssets,
        du,
        onRowClick,
      };
    },
  });
